import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const SECTIONS = [
    {
        title: "1. Customer Service & Transparent Communication",
        points: [
            "Associates must respond to customer enquiries promptly and professionally.",
            "Always communicate accurate information and set clear expectations regarding package prices, inclusions, exclusions and travel timelines.",
            "Do not make commitments or promises to customers that have not been confirmed by the Ghumne Chaley team.",
            "Ensure customers receive clear and honest information throughout the enquiry and booking process.",
        ],
    },
    {
        title: "2. Accurate Promotion & Responsible Marketing",
        points: [
            "Promote only packages approved by Ghumne Chaley, using the latest official information and promotional material.",
            "Do not advertise unapproved packages, unauthorised discounts or unverified offers.",
            "If any package detail, price or inclusion is uncertain, confirm it with the Ghumne Chaley team before sharing it with customers.",
            "Do not misrepresent the company, its services or its offerings.",
        ],
    },
    {
        title: "3. Customer Privacy & Data Protection",
        points: [
            "Customer names, contact details, travel preferences and other personal information must be treated as confidential.",
            "Customer information must be used only for the enquiry or booking for which it was provided.",
            "Associates must not share, misuse, sell or disclose customer information to unauthorised individuals or organisations.",
            "Take reasonable precautions to protect customer information from unauthorised access.",
        ],
    },
    {
        title: "4. Secure Account Handling & Payment Safety",
        points: [
            "Keep your Associate ID, login credentials and account information confidential.",
            "Do not share your login credentials or allow unauthorised individuals to access your associate account.",
            "All customer payments must be made through official Ghumne Chaley payment channels.",
            "Associates must never collect or redirect customer payments into personal bank accounts, UPI IDs or other unauthorised payment channels.",
            "Report any suspected fraudulent activity or account misuse to the Ghumne Chaley team immediately.",
        ],
    },
    {
        title: "5. Complaint Escalation & Professional Conduct",
        points: [
            "Any customer complaint, booking dispute, incorrect information or service-related concern must be escalated to the Ghumne Chaley team immediately.",
            "Associates must maintain professional, respectful and courteous communication with customers and company representatives.",
            "Do not engage in misleading, abusive, discriminatory or unethical conduct.",
            "Associates must cooperate with the company in resolving customer concerns.",
        ],
    },
    {
        title: "6. Brand Assets & Intellectual Property",
        points: [
            "Use the official Ghumne Chaley logo, associate badges, creatives and promotional materials exactly as supplied.",
            "Do not alter, redraw, modify or repurpose official brand assets without prior written approval.",
            "All company-provided content, creatives, package information and brand assets remain the intellectual property of Ghumne Chaley or their respective owners.",
            "Do not use the company's name, logo or identity for any unauthorised activity.",
        ],
    },
    {
        title: "7. Performance Tracking & Compliance",
        points: [
            "Referrals, bookings and eligible revenue will be tracked against the associate's registered Associate ID.",
            "Associates are responsible for sharing their correct referral details so that eligible referrals can be attributed accurately.",
            "Performance and programme compliance may be reviewed periodically by Ghumne Chaley.",
            "Sustained underperformance, fraudulent referrals, policy violations or misconduct may result in suspension or deactivation of the associate account, subject to company review.",
        ],
    },
    {
        title: "8. Commission Credit & Payment Conditions",
        points: [
            "Applicable commission percentages depend on the associate category.",
            "Commission is credited only after the referred tour has been successfully completed.",
            "A booking confirmation or advance payment alone does not make the commission eligible for credit.",
            "Commission will be calculated and recorded against the eligible completed booking under the associate's registered Associate ID, after the company's booking and payment verification.",
            "Cancelled, refunded, fraudulent or otherwise ineligible bookings will not qualify for commission.",
            "The commission amount and applicable status will be reflected in the associate account as per the company's records and commission policy.",
            "Any discrepancy in referral attribution or commission records must be reported to the Ghumne Chaley team for verification.",
        ],
    },
    {
        title: "9. Complimentary Family Trip Benefits",
        points: [
            "Eligible Travel Ambassadors and Privilege Members may receive complimentary family trip benefits upon achieving their applicable annual revenue targets:",
            "Travel Ambassador: One complimentary family trip worth up to ₹1,00,000 upon achieving the annual revenue target of ₹25 lakh.",
            "Privilege Member: Two complimentary family trips, each worth up to ₹1,00,000, upon achieving the annual revenue target of ₹75 lakh.",
        ],
        sub: "Conditions for availing the benefit:",
        sub_points: [
            "The applicable annual revenue target must be achieved and verified by Ghumne Chaley.",
            "The associate must inform the Ghumne Chaley team at least 2 months (60 days) before the proposed travel date to request redemption of the complimentary family trip.",
            "Redemption is subject to prior coordination, availability and written confirmation from the Ghumne Chaley team.",
            "The trip must be planned and finalised through Ghumne Chaley. Associates should not make independent bookings or commitments against this benefit before receiving confirmation.",
            "The complimentary trip benefit is subject to the stated maximum value. Any additional cost beyond the approved benefit value, if applicable, will be communicated to the associate before confirmation.",
            "The benefit is non-transferable and cannot be exchanged for cash unless expressly approved by the company in writing.",
        ],
    },
    {
        title: "10. General Programme Conditions",
        points: [
            "Ghumne Chaley reserves the right to review, amend or update the Associate Programme, its benefits, commission policies and these Terms & Conditions when required.",
            "Any material changes will be communicated through the company's official communication channels.",
            "Associates are expected to remain informed about the latest programme guidelines.",
            "Ghumne Chaley reserves the right to investigate suspected violations and take appropriate action, including suspension or termination of associate privileges.",
        ],
    },
];

export function PartnerTerms({ compact = false }) {
    const [open, setOpen] = useState(null);
    return (
        <section className={compact ? "py-6" : "py-12 sm:py-16"} data-testid="partner-terms">
            <div className={compact ? "" : "max-w-4xl mx-auto px-4 sm:px-6"}>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-saffron text-center">Legal</p>
                <h2 className={`mt-2 font-display font-black text-navy text-center ${compact ? "text-xl" : "text-2xl sm:text-3xl"}`} data-testid="partner-terms-heading">
                    Terms &amp; Conditions
                </h2>
                <p className="mt-2 text-xs text-stone-500 text-center">Ghumne Chaley Associates &amp; Partners</p>
                {!compact && (
                    <p className="mt-4 text-xs text-stone-500 leading-relaxed text-center max-w-2xl mx-auto">
                        These Terms &amp; Conditions apply to all Ghumne Chaley associate categories: Explorer, Travel Insider,
                        Travel Ambassador and Privilege Member. By registering or participating in the Ghumne Chaley Associate
                        Programme, members agree to follow the guidelines outlined below.
                    </p>
                )}

                <div className="mt-8 divide-y divide-stone-200 rounded-2xl border border-stone-200 bg-white overflow-hidden">
                    {SECTIONS.map((s, i) => (
                        <div key={i}>
                            <button
                                type="button"
                                onClick={() => setOpen(open === i ? null : i)}
                                data-testid={`terms-section-${i}`}
                                className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-cream/60 transition-colors"
                            >
                                <span className="font-bold text-navy text-xs sm:text-sm">{s.title}</span>
                                {open === i ? <ChevronUp className="h-4 w-4 text-saffron shrink-0" /> : <ChevronDown className="h-4 w-4 text-stone-400 shrink-0" />}
                            </button>
                            {open === i && (
                                <div className="px-5 pb-4 text-xs text-stone-600 leading-relaxed" data-testid={`terms-body-${i}`}>
                                    <ul className="list-disc pl-5 space-y-1.5">
                                        {s.points.map((p, j) => <li key={j}>{p}</li>)}
                                    </ul>
                                    {s.sub && (
                                        <div className="mt-3">
                                            <p className="font-bold text-navy text-xs">{s.sub}</p>
                                            <ul className="mt-1.5 list-disc pl-5 space-y-1.5">
                                                {s.sub_points.map((p, j) => <li key={j}>{p}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <p className="mt-8 text-center font-script text-2xl text-gold">Har Safar, Ek Kahani.</p>
                <p className="mt-1 text-center text-[11px] text-stone-400">Chaley — honestly, proudly, together.</p>
            </div>
        </section>
    );
}
